import React, { useState, useEffect, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BASE_API_URL } from '../helpers/Config';
import toast from 'react-hot-toast';

const TradeList = ({ theme = 'dark' }) => {
    const [markets, setMarkets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [imageLoaded, setImageLoaded] = useState({});
    const navigate = useNavigate();

    // Theme colors for both dark and light themes
    const themeColors = {
        dark: {
            bgPrimary: '#0f172a',
            bgSecondary: '#1e293b',
            bgCard: '#1e293b',
            textPrimary: '#f8fafc',
            textSecondary: '#cbd5e1',
            border: '#334155',
            accent: '#3b82f6',
            positive: '#10b981',
            positiveBg: 'rgba(16, 185, 129, 0.15)',
            negative: '#ef4444',
            negativeBg: 'rgba(239, 68, 68, 0.15)',
            hover: '#2d3748'
        },
        light: {
            bgPrimary: '#f8fafc',
            bgSecondary: '#ffffff',
            bgCard: '#ffffff',
            textPrimary: '#0f172a',
            textSecondary: '#64748b',
            border: '#e2e8f0',
            accent: '#3b82f6',
            positive: '#059669',
            positiveBg: 'rgba(5, 150, 105, 0.15)',
            negative: '#dc2626',
            negativeBg: 'rgba(220, 38, 38, 0.15)',
            hover: '#f1f5f9'
        }
    };

    const currentTheme = themeColors[theme] || themeColors.dark;

    const FetchMyTrades = async () => {
        try {
            setLoading(true);
            const response = await axios.post(`${BASE_API_URL}/trade/trade-list`, {
                token: localStorage.getItem('a5srf4wer7w1cd'),
            });
            const data = response?.data?.data || [];
            setMarkets(data);

            const initialImageState = {};
            data.forEach(market => {
                initialImageState[market.entity_id] = false;
            });
            setImageLoaded(initialImageState);

        } catch (error) {
            console.error('Error fetching trades:', error);
            toast.error('Failed to fetch trade list', { position: 'bottom-center' });
        } finally {
            setLoading(false);
        }
    };

    // Handle card click navigation - all items are clickable now
    const handleCardClick = (market) => {
        if (!market.is_demo) {
            navigate(`/trade/${market.entity_id}`);
        }
        // Demo items are also clickable but won't navigate
    };

    // Handle image load
    const handleImageLoad = (entityId) => {
        setImageLoaded(prev => ({
            ...prev,
            [entityId]: true
        }));
    };

    // Handle image error
    const handleImageError = (entityId) => {
        setImageLoaded(prev => ({
            ...prev,
            [entityId]: true
        }));
    };

    useEffect(() => {
        FetchMyTrades();
    }, []);

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.01
            }
        }
    };

    const itemVariants = {
        hidden: {
            opacity: 0,
            x: -20,
            scale: 0.98
        },
        visible: {
            opacity: 1,
            x: 0,
            scale: 1,
            transition: {
                type: "spring",
                stiffness: 100,
                damping: 12
            }
        }
    };

    const hoverVariants = {
        hover: {
            y: -1,
            scale: 1.01,
            transition: {
                type: "spring",
                stiffness: 400,
                damping: 10
            }
        }
    };

    // Format functions
    const formatPrice = (price) => {
        const numPrice = parseFloat(price);
        if (isNaN(numPrice)) return price;

        if (numPrice >= 1000) {
            return `$${numPrice.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })}`;
        }

        if (numPrice < 0.01) {
            return `$${numPrice.toFixed(6)}`;
        }

        return `$${numPrice.toFixed(2)}`;
    };

    const formatPercentage = (percentage) => {
        const num = parseFloat(percentage);
        if (isNaN(num)) return percentage;
        return `${num >= 0 ? '+' : ''}${num.toFixed(2)}%`;
    };

    // Skeleton Loader
    const SkeletonLoader = () => {
        return (
            <div className="h-100 w-100" style={{ padding: '12px', background: currentTheme.bgPrimary }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div style={{ width: '120px', height: '24px', background: currentTheme.bgSecondary, borderRadius: '6px' }}></div>
                    <div style={{ width: '80px', height: '16px', background: currentTheme.bgSecondary, borderRadius: '6px' }}></div>
                </div>

                {[...Array(15)].map((_, index) => (
                    <div key={index} className="mb-2">
                        <div style={{
                            background: currentTheme.bgCard,
                            borderRadius: '8px',
                            padding: '12px',
                            border: `1px solid ${currentTheme.border}`
                        }}>
                            <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center" style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{
                                        width: '36px',
                                        height: '36px',
                                        background: currentTheme.bgSecondary,
                                        borderRadius: '50%',
                                        marginRight: '12px'
                                    }}></div>
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        <div style={{
                                            width: '80px',
                                            height: '14px',
                                            background: currentTheme.bgSecondary,
                                            borderRadius: '4px',
                                            marginBottom: '6px'
                                        }}></div>
                                        <div style={{
                                            width: '60px',
                                            height: '11px',
                                            background: currentTheme.bgSecondary,
                                            borderRadius: '4px'
                                        }}></div>
                                    </div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{
                                        width: '70px',
                                        height: '14px',
                                        background: currentTheme.bgSecondary,
                                        borderRadius: '4px',
                                        marginLeft: 'auto',
                                        marginBottom: '6px'
                                    }}></div>
                                    <div style={{
                                        width: '50px',
                                        height: '11px',
                                        background: currentTheme.bgSecondary,
                                        borderRadius: '4px',
                                        marginLeft: 'auto'
                                    }}></div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    if (loading) {
        return <SkeletonLoader />;
    }

    return (
        <div style={{
            background: currentTheme.bgPrimary,
            color: currentTheme.textPrimary,
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Header - Compact */}
            <div className="d-flex justify-content-between align-items-center mb-2" style={{ padding: '12px 12px 0 12px' }}>
                <div>
                    <h3 style={{
                        fontWeight: '600',
                        margin: 0,
                        fontSize: '18px',
                        lineHeight: '1.2',
                        color: currentTheme.textPrimary
                    }}>
                        Markets
                    </h3>
                    <p style={{
                        color: currentTheme.textSecondary,
                        margin: 0,
                        fontSize: '11px',
                        lineHeight: '1.2'
                    }}>
                        {markets.length} cryptocurrencies
                    </p>
                </div>

                <div className="d-flex align-items-center gap-1">
                    {/* Live Indicator */}
                    <div className="d-flex align-items-center gap-1">
                        <div style={{
                            width: '6px',
                            height: '6px',
                            background: currentTheme.positive,
                            borderRadius: '50%',
                            animation: 'pulse 2s infinite'
                        }}></div>
                        <span style={{
                            color: currentTheme.positive,
                            fontSize: '11px',
                            fontWeight: '500',
                            lineHeight: '1.2'
                        }}>
                            LIVE
                        </span>
                    </div>
                </div>
            </div>

            {/* Markets List - Takes remaining space */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: '8px 12px'
            }}>
                <AnimatePresence>
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        key={theme} // Force re-animation on theme change
                    >
                        {markets.map((market, index) => {
                            const isPositive = parseFloat(market.change_percentage) >= 0;

                            return (
                                <motion.div
                                    key={`${market.entity_id}-${theme}`} // Unique key for theme changes
                                    variants={itemVariants}
                                    whileHover={hoverVariants.hover}
                                    custom={index}
                                >
                                    <div
                                        onClick={() => handleCardClick(market)}
                                        style={{
                                            background: currentTheme.bgCard,
                                            borderRadius: '8px',
                                            padding: '12px',
                                            marginBottom: '8px',
                                            border: `1px solid ${currentTheme.border}`,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            position: 'relative',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        {/* Hover effect */}
                                        <div style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            background: `linear-gradient(90deg, transparent, ${currentTheme.hover}15, transparent)`,
                                            opacity: 0,
                                            transition: 'opacity 0.3s'
                                        }} className="hover-effect"></div>

                                        <div className="d-flex align-items-center justify-content-between">
                                            {/* Left side - Asset info */}
                                            <div className="d-flex align-items-center" style={{ flex: 1, minWidth: 0, marginRight: '16px' }}>
                                                <div style={{ position: 'relative', flexShrink: 0 }}>
                                                    {!imageLoaded[market.entity_id] && (
                                                        <div style={{
                                                            width: '36px',
                                                            height: '36px',
                                                            background: currentTheme.bgSecondary,
                                                            borderRadius: '50%'
                                                        }}></div>
                                                    )}
                                                    <img
                                                        src={market.image}
                                                        alt={market.entity_name}
                                                        style={{
                                                            width: '36px',
                                                            height: '36px',
                                                            borderRadius: '50%',
                                                            objectFit: 'cover',
                                                            opacity: imageLoaded[market.entity_id] ? 1 : 0,
                                                            transition: 'opacity 0.3s',
                                                            position: imageLoaded[market.entity_id] ? 'relative' : 'absolute',
                                                            border: `1px solid ${currentTheme.border}`
                                                        }}
                                                        onLoad={() => handleImageLoad(market.entity_id)}
                                                        onError={() => handleImageError(market.entity_id)}
                                                    />
                                                </div>
                                                <div style={{ minWidth: 0, flex: 1, marginLeft: '12px' }}>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        marginBottom: '2px'
                                                    }}>
                                                        <h6 style={{
                                                            margin: 0,
                                                            fontWeight: '500',
                                                            fontSize: '14px',
                                                            lineHeight: '1.2',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            color: currentTheme.textPrimary
                                                        }}>
                                                            {market.entity_name}
                                                        </h6>
                                                    </div>
                                                    <p style={{
                                                        margin: 0,
                                                        color: currentTheme.textSecondary,
                                                        fontSize: '12px',
                                                        lineHeight: '1.2',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        textTransform: 'uppercase',
                                                        fontWeight: '400'
                                                    }}>
                                                        {market.entity_id}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Right side - Price and change percentage */}
                                            <div style={{
                                                textAlign: 'right',
                                                flexShrink: 0,
                                                minWidth: '100px'
                                            }}>
                                                {/* Price */}
                                                <div style={{
                                                    fontWeight: '600',
                                                    fontSize: '14px',
                                                    lineHeight: '1.2',
                                                    whiteSpace: 'nowrap',
                                                    marginBottom: '4px',
                                                    color: currentTheme.textPrimary
                                                }}>
                                                    {formatPrice(market.current_price)}
                                                </div>

                                                {/* Change Percentage Badge */}
                                                <div style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '4px',
                                                    background: isPositive ? currentTheme.positiveBg : currentTheme.negativeBg,
                                                    color: isPositive ? currentTheme.positive : currentTheme.negative,
                                                    fontWeight: '600',
                                                    fontSize: '12px',
                                                    lineHeight: '1.2',
                                                    whiteSpace: 'nowrap',
                                                    padding: '4px 8px',
                                                    borderRadius: '6px',
                                                    border: `1px solid ${isPositive ? currentTheme.positive + '20' : currentTheme.negative + '20'}`,
                                                    minWidth: '70px'
                                                }}>
                                                    <i className={`bi bi-${isPositive ? 'arrow-up' : 'arrow-down'}`}
                                                        style={{ fontSize: '10px' }}></i>
                                                    {formatPercentage(market.change_percentage)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* CSS Styles */}
            <style jsx global>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }

                .hover-effect {
                    pointer-events: none;
                }

                div:hover > .hover-effect {
                    opacity: 1;
                }

                ::-webkit-scrollbar {
                    width: 4px;
                }

                ::-webkit-scrollbar-track {
                    background: ${currentTheme.bgSecondary};
                    border-radius: 4px;
                }

                ::-webkit-scrollbar-thumb {
                    background: ${currentTheme.border};
                    border-radius: 4px;
                }

                ::-webkit-scrollbar-thumb:hover {
                    background: ${currentTheme.accent};
                }

                button:not(:disabled):hover {
                    transform: translateY(-1px);
                }

                /* Smooth transition for theme changes */
                * {
                    transition: background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease;
                }
            `}</style>
        </div>
    );
};

export default TradeList;